import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Lock, CreditCard, Shield } from "lucide-react";
import { useAppContext } from "../AppContext";
import axios from "axios";
import { Select, Button, Modal } from "antd";
import TermsAndCondition from "./TermsAndCondition";


const API_BASE = "https://app.commercialcleaningleadsforyou.com";
const NEW_BUSINESS_API_BASE = "https://app.commercialcleaningleadsforyou.com";
const STATE_NAME_TO_CODE = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA", Colorado: "CO",
  Connecticut: "CT", Delaware: "DE", "District of Columbia": "DC", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA", Kansas: "KS", Kentucky: "KY",
  Louisiana: "LA", Maine: "ME", Maryland: "MD", Massachusetts: "MA", Michigan: "MI", Minnesota: "MN",
  Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH",
  "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT", Virginia: "VA",
  Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY", "Puerto Rico": "PR",
};

const PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

async function fetchPublishableKeyIfNeeded() {
  if (PUBLISHABLE_KEY) return PUBLISHABLE_KEY;
  const res = await fetch(`${API_BASE}/api/config`);
  if (!res.ok) throw new Error("Cannot reach /api/config.");
  const data = await res.json();
  if (!data.publishableKey)
    throw new Error("Server missing STRIPE_PUBLISHABLE_KEY.");
  return data.publishableKey;
}

/* -------------------- Main Form -------------------- */

const pricingData = [
  { state: "Alabama", count: 400, cost: "$29/month" },
  { state: "Alaska", count: 8, cost: "$29/month" },
  { state: "Arizona", count: 268, cost: "$29/month" },
  { state: "Arkansas", count: 261, cost: "$29/month" },
  { state: "California", count: "", cost: "$99/month" },
  { state: "Colorado", count: 567, cost: "$79/month" },
  { state: "Connecticut", count: 335, cost: "$29/month" },
  { state: "Delaware", count: 134, cost: "$29/month" },
  { state: "Florida", count: 2855, cost: "$99/month" },
  { state: "Georgia", count: 1544, cost: "$99/month" },
  { state: "Hawaii", count: 99, cost: "$29/month" },
  { state: "Idaho", count: 81, cost: "$29/month" },
  { state: "Illinois", count: 1354, cost: "$99/month" },
  { state: "Indiana", count: 585, cost: "$79/month" },
  { state: "Iowa", count: 207, "cost": "$29/month" },
  { state: "Kansas", count: 134, "cost": "$29/month" },
  { state: "Kentucky", count: 321, "cost": "$29/month" },
  { state: "Louisiana", count: 319, "cost": "$29/month" },
  { state: "Maine", count: 775, "cost": "$79/month" },
  { state: "Maryland", count: 626, "cost": "$79/month" },
  { state: "Massachusetts", count: 14, "cost": "$29/month" },
  { state: "Michigan", count: 1078, "cost": "$99/month" },
  { state: "Minnesota", count: 472, "cost": "$79/month" },
  { state: "Mississippi", count: 208, "cost": "$29/month" },
  { state: "Missouri", count: 365, "cost": "$29/month" },
  { state: "Montana", count: 83, "cost": "$29/month" },
  { state: "Nebraska", count: 99, "cost": "$29/month" },
  { state: "Nevada", count: 200, "cost": "$29/month" },
  { state: "New Hampshire", count: 17, "cost": "$29/month" },
  { state: "New Jersey", count: 1713, "cost": "$99/month" },
  { state: "New Mexico", count: 200, "cost": "$29/month" },
  { state: "New York", count: 1217, "cost": "$99/month" },
  { state: "North Carolina", count: 795, "cost": "$79/month" },
  { state: "North Dakota", count: 172, "cost": "$29/month" },
  { state: "Ohio", count: 740, "cost": "$79/month" },
  { state: "Oklahoma", count: 201, "cost": "$29/month" },
  { state: "Oregon", count: 339, "cost": "$29/month" },
  { state: "Pennsylvania", count: 1280, "cost": "$99/month" },
  { state: "Rhode Island", count: 62, "cost": "$29/month" },
  { state: "South Carolina", count: 640, "cost": "$79/month" },
  { state: "South Dakota", count: 112, "cost": "$29/month" },
  { state: "Tennessee", count: 839, "cost": "$99/month" },
  { state: "Texas", count: 2175, "cost": "$99/month" },
  { state: "Utah", count: 210, "cost": "$79/month" },
  { state: "Vermont", count: 650, "cost": "$79/month" },
  { state: "Virginia", count: 10, "cost": "$29/month" },
  { state: "Washington", count: 709, "cost": "$79/month" },
  { state: "West Virginia", count: 326, "cost": "$29/month" },
  { state: "Wisconsin", count: 144, "cost": "$29/month" },
  { state: "Wyoming", count: 74, "cost": "$29/month" },
];

function PrettyForm() {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const {
    name,
    setName,
    email,
    setEmail,
    phone,
    setPhone,
    line1,
    setLine1,
    city,
    setCity,
    state,
    setState,
    postal,
    setPostal,
    zipcodes,
    setZipcodes,
    buyNow,
    setBuyNow,
    selectedPrice,
    setSelectedPrice,
    leadsCount,
    setLeadsCount,
    billingFrequency,
    setBillingFrequency,
    clientSecret,
    setClientSecret,
    stateAverages,
    setStateAverage,
    setPaymentId: setContextPaymentId,
  } = useAppContext();

  const [toast, setToast] = useState({ open: false, text: "" });
  const [successOpen, setSuccessOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ip, setIp] = useState();
  const [error, setError] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [downloadAccepted, setDownloadAccepted] = useState(false);
  const [downloadShowTerms, setDownloadShowTerms] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [averageLoading, setAverageLoading] = useState(false);

  // Promo code states
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeValidating, setPromoCodeValidating] = useState(false);
  const [promoCodeValid, setPromoCodeValid] = useState(false);
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [promoCodeError, setPromoCodeError] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const rateLimitTimerRef = useRef(null);

  useEffect(() => {
    if (!toast.open) return;
    const t = setTimeout(() => setToast({ open: false, text: "" }), 6000);
    return () => clearTimeout(t);
  }, [toast.open]);

  const showError = (msg) => setToast({ open: true, text: msg });
  const closeError = () => setToast({ open: false, text: "" });

  const [loadings, setLoadings] = useState(false);

  const getFilenameFromHeaders = (resp) => {
    const cd = resp.headers.get("content-disposition");
    if (!cd) return null;
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
    return decodeURIComponent(m?.[1] || m?.[2] || "");
  };

  useEffect(() => {
    const getIpDetails = async () => {
      try {
        // Make a request without the token
        const response = await axios.get("https://ipinfo.io/json");
        setIp(response.data);
      } catch (error) {
        console.error("Error fetching IP details:", error);
      }
    };

    getIpDetails();
  }, []);
  console.log(ip, 'ip');

  // Promo code logic
  const validatePromoCode = async (code) => {
    if (!code || code.trim() === "") {
      setPromoCodeValid(false);
      setPromoCodeApplied(false);
      setPromoCodeDiscount(0);
      setPromoCodeError("");
      setRateLimited(false);
      return;
    }

    setPromoCodeValidating(true);
    setPromoCodeError("");
    setRateLimited(false);

    try {
      const response = await fetch(`${API_BASE}/api/validate-promo-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promoCode: code.trim() }),
      });

      const data = await response.json();

      if (data.valid) {
        setPromoCodeValid(true);
        setPromoCodeApplied(true);
        setPromoCodeDiscount(data.discount);
        setPromoCodeError("");
        setRateLimited(false);
        setRemainingTime(0);
        localStorage.removeItem('promoRateLimitExpiresAt');
        if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);

        await updatePaymentIntentWithDiscount(data.discount);
      } else {
        setPromoCodeValid(false);
        setPromoCodeApplied(false);
        setPromoCodeDiscount(0);
        setPromoCodeError(data.message || data.error || "Invalid promo code");

        if (data.rateLimited && data.expiresAt) {
          const expiresAt = data.expiresAt;
          setRateLimited(true);
          localStorage.setItem('promoRateLimitExpiresAt', expiresAt.toString());
          startCountdownTimer(expiresAt);
        } else {
          setRateLimited(false);
          setRemainingTime(0);
          setRemainingAttempts(data.remainingAttempts || 5);
        }
        await updatePaymentIntentWithDiscount(0);
      }
    } catch (error) {
      console.error("Error validating promo code:", error);
      setPromoCodeValid(false);
      setPromoCodeApplied(false);
      setPromoCodeDiscount(0);
      setPromoCodeError("Failed to validate promo code. Please try again.");
      await updatePaymentIntentWithDiscount(0);
    } finally {
      setPromoCodeValidating(false);
    }
  };

  const updatePaymentIntentWithDiscount = async (discountPercent) => {
    let paymentIntentId = null;
    if (import.meta.env.VITE_APP_CLIENT_SECRET || clientSecret) {
      const secret = import.meta.env.VITE_APP_CLIENT_SECRET || clientSecret;
      const parts = secret.split('_secret_');
      if (parts.length === 2) paymentIntentId = parts[0];
    }

    if (!paymentIntentId) return;

    const discountedVal = Math.round(selectedPrice * (1 - discountPercent));

    try {
      await fetch(`${API_BASE}/api/update-payment-intent-amount-newBusiness`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentIntentId,
          amount: discountedVal,
          originalAmount: selectedPrice,
          couponCode: promoCode
        }),
      });
    } catch (error) {
      console.error("Error updating payment amount:", error);
    }
  };

  const handlePromoCodeChange = (e) => {
    const value = e.target.value;
    setPromoCode(value);
    if (value.trim() === "") {
      setPromoCodeValid(false);
      setPromoCodeApplied(false);
      setPromoCodeDiscount(0);
      setPromoCodeError("");
      setRateLimited(false);
      updatePaymentIntentWithDiscount(0);
    }
  };

  const startCountdownTimer = (expiresAt) => {
    if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);
    const updateTimer = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setRemainingTime(diff);
      if (diff <= 0) {
        clearInterval(rateLimitTimerRef.current);
        setRateLimited(false);
        localStorage.removeItem('promoRateLimitExpiresAt');
      }
    };
    updateTimer();
    rateLimitTimerRef.current = setInterval(updateTimer, 1000);
  };

  useEffect(() => {
    const storedExpiresAt = localStorage.getItem('promoRateLimitExpiresAt');
    if (storedExpiresAt) {
      const expiresAt = parseInt(storedExpiresAt, 10);
      if (expiresAt > Date.now()) {
        setRateLimited(true);
        startCountdownTimer(expiresAt);
      } else {
        localStorage.removeItem('promoRateLimitExpiresAt');
      }
    }
    return () => {
      if (rateLimitTimerRef.current) clearInterval(rateLimitTimerRef.current);
    };
  }, []);

  const discountedAmount = Math.round(selectedPrice * (1 - (promoCodeApplied ? promoCodeDiscount : 0)));

  async function postDownloadStatus(paymentId, status) {
    if (!paymentId) return;
    try {
      await fetch(`${API_BASE}/api/update-download-status-newBusiness`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentId, downloadStatus: status }),
      });
    } catch (e) {
      console.warn("update-download-status failed:", e);
    }
  }

  const handleStateSelect = (stateName) => {
    const s = pricingData.find(item => item.state === stateName);
    if (s) {
      setZipcodes(stateName);
      const costInt = parseInt(s.cost.replace(/[^0-9]/g, "")) || 199;
      setSelectedPrice(costInt * 100);
      setLeadsCount(typeof s.count === 'number' ? s.count : 1000);
      if (promoCodeApplied) {
        updatePaymentIntentWithDiscount(promoCodeDiscount);
      }
    }
  };

  // Helper function to format numbers with commas
  const formatCount = (count) => {
    if (count === "" || count === null || count === undefined) {
      return "";
    }
    return typeof count === "number" ? count.toLocaleString() : count;
  };

  const handleDownload = async (e) => {
    e.preventDefault();

    if (!/^[a-zA-Z0-9,\s-]{3,100}$/.test(zipcodes)) {
      alert("Please enter valid zipcode(s) or state.");
      return;
    }

    if (!downloadAccepted) {
      alert("Please accept the Terms & Conditions to download.");
      return;
    }

    setLoadings?.(true);

    // ensure we only post one status
    let statusPosted = false;
    const postOnce = async (status) => {
      if (statusPosted) return;
      statusPosted = true;
      await postDownloadStatus(paymentId, status);
    };

    try {
      const resp = await fetch(`${API_BASE}/api/newBusiness-download-csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          zipcode: zipcodes,
          email,
          paymentId,
        }),
      });

      if (!resp.ok) {
        await postOnce("No");
        const text = await resp.text();
        let message = text;
        try {
          message = JSON.parse(text).error || message;
        } catch { }
        throw new Error(message || `Request failed: ${resp.status}`);
      }

      // ✅ 200 OK → immediately mark as Yes
      await postOnce("Yes");

      // Then download the CSV
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const now = new Date();
      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const monthYear = `${monthNames[now.getMonth()]}_${now.getFullYear()}`;
      const filename =
        getFilenameFromHeaders(resp) ||
        `new-business-${(zipcodes || 'data').replace(/[^\w\s-]/g, '')}_${monthYear}.csv`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      // Download complete → redirect to thank-you page for analytics
      navigate("/thank-you");

      // Optional: reset form/UI
      setEmail?.("");
      setCity?.("");
      setLine1?.("");
      setPhone?.("");
      setPostal?.("");
      setState?.("");
      setName?.("");
      // setBuyNow?.(false);
      setSuccessOpen?.(false);
    } catch (err) {
      console.error(err);
      // ensure No is posted if we haven't yet
      await postOnce("No");
      alert("Error downloading file");
    } finally {
      setLoadings?.(false);
    }
  };

  function ErrorToast({ open, text, onClose }) {
    if (!open) return null;
    return (
      <div className="fixed z-50 bottom-6 inset-x-0 px-4">
        <div className="mx-auto max-w-md">
          <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg px-4 py-3">
            <div className="flex items-center">
              <span className="mr-3 text-xl">⚠️</span>
              <div className="font-medium">{text}</div>
            </div>
            <button
              onClick={onClose}
              className="text-white/90 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleConfirmPayment = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    if (!accepted) {
      showError("Please accept terms and conditions.");
      return;
    }

    const submitResult = await elements.submit();
    if (submitResult?.error) {
      showError(submitResult.error.message);
      return;
    }

    setSubmitting(true);
    try {
      // Extract paymentIntentId from clientSecret
      let paymentIntentId = null;
      if (clientSecret) {
        const parts = clientSecret.split('_secret_');
        if (parts.length === 2) paymentIntentId = parts[0];
      }

      const usDate = new Date().toLocaleString("en-US", { timeZone: "America/Chicago" });
      const now = new Date(usDate);
      const pad = (n) => n.toString().padStart(2, "0");
      const orderno = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      setOrderNumber(orderno);

      const createRes = await fetch(
        `${API_BASE}/api/create-newBusiness-payment-onetime`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            name,
            amount: discountedAmount,
            originalAmount: selectedPrice,
            currency: "usd",
            glsZipCode: zipcodes,
            phone,
            address: line1,
            city,
            state,
            zipcode: postal,
            user_city: ip?.city,
            user_postal: ip?.postal,
            user_region: ip?.region,
            user_loc: ip?.loc,
            user_ip: ip?.ip,
            orderno,
            couponCode: promoCodeApplied ? promoCode : null,
            billingFrequency,
            paymentIntentId, // Pass the PI ID to link and update amount
          }),
        }
      );

      const createData = await createRes.json().catch(() => null);
      if (!createRes.ok || !createData?.paymentId)
        throw new Error(createData?.error || "Payment creation failed");

      const paymentId = createData.paymentId;
      setPaymentId(paymentId);
      // Removed setClientSecret(createData.clientSecret) to prevent disruptive remount during confirmation

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { receipt_email: email },
        redirect: "if_required",
      });

      if (error) throw new Error(error.message);
      if (paymentIntent?.status !== "succeeded")
        throw new Error(
          `Payment not completed (status: ${paymentIntent?.status}).`
        );

      await fetch(
        `${API_BASE}/api/sync-payment-intent-newBusiness`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            paymentId,
          }),
        }
      );
      setContextPaymentId?.(paymentId);
      navigate("/download");
    } catch (err) {
      showError(err?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromoCodeSubmit = () => {
    if (promoCode.trim()) validatePromoCode(promoCode);
  };


  // Use cached 12-month average when available; otherwise fetch once and cache
  useEffect(() => {
    const stateName = zipcodes?.trim();
    const stateCode = stateName ? STATE_NAME_TO_CODE[stateName] ?? (stateName.length === 2 ? stateName.toUpperCase() : null) : null;
    if (!stateCode) return;
    const cached = stateAverages?.[stateCode];
    if (cached != null) {
      setLeadsCount(cached);
      setAverageLoading(false);
      return;
    }
    let cancelled = false;
    setAverageLoading(true);
    fetch(`${NEW_BUSINESS_API_BASE}/api/new-business-state-average`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        state: stateCode,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Failed to fetch average"))))
      .then((data) => {
        if (!cancelled && data.average_12_month != null) {
          setStateAverage(stateCode, data.average_12_month);
          setLeadsCount(data.average_12_month);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setAverageLoading(false); });
    return () => { cancelled = true; };
  }, [zipcodes, setLeadsCount, stateAverages, setStateAverage]);

  const handleAgree = () => {
    setAccepted(true);
    setShowTerms(false);
  };

  const handleDownloadAgree = () => {
    setDownloadAccepted(true);
    setDownloadShowTerms(false);
  };

  return (
    <div>
      <ErrorToast open={toast.open} text={toast.text} onClose={closeError} />

      {buyNow === "payment" && (
        <div className="max-w-3xl mx-auto mb-8 w-full px-4">
          <div className="shadow-2xl border-6 border-[#F09900] rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
            <div className="bg-[#F09900] p-4 text-white">
              <div className="flex justify-between items-center gap-4">
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-bold">Payment Details</h2>
                  <p className="text-indigo-100">Secure payment processing</p>
                </div>
                <Shield className="h-10 w-10 text-white/80 shrink-0" />
              </div>
              <div className="mt-2 bg-[#d4e2e9] rounded-xl p-4 backdrop-blur-sm">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-[#4E6978] text-lg">Subtotal</div>
                      <div className="text-[#4E6978] text-2xl font-bold">${(selectedPrice / 100).toFixed(2)}</div>
                    </div>
                    <CreditCard className="h-8 w-8 text-[#4E6978]" />
                  </div>
                  {promoCodeApplied && (
                    <div className="pt-2 border-t border-[#4D6877]/20">
                      <div className="flex justify-between items-center text-[#4E6978]">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {Math.round(promoCodeDiscount * 100)}% Discount ({promoCode.trim().toUpperCase()})
                          </span>
                          <span className="text-xs bg-[#F09900] text-white px-2 py-0.5 rounded-full font-semibold">Applied</span>
                        </div>
                        <div className="text-lg font-semibold">-${((selectedPrice * promoCodeDiscount) / 100).toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                  <div className="pt-2 border-t-2 border-[#4D6877]/30">
                    <div className="flex justify-between items-center">
                      <div className="text-[#4E6978] text-lg font-semibold">Total amount</div>
                      <div className="text-[#4E6978] text-2xl font-bold">
                        ${(discountedAmount / 100).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleConfirmPayment}>
                <div className="space-y-5">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-md text-[#4E6978] font-medium text-center block">
                        Send me Brand New Businesses every Week for the state of:
                      </label>
                      <div className="w-full flex justify-center items-center mt-2">
                        <Select
                          showSearch
                          placeholder="Select a state"
                          className="w-full h-12 custom-select"
                          value={zipcodes || undefined}
                          onChange={handleStateSelect}
                          optionFilterProp="label"
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          options={pricingData.map(s => ({
                            label: s.state,
                            value: s.state
                          }))}
                        />
                      </div>
                      <div className="my-4">
                      <label className="text-md text-[#4E6978] font-medium block leading-6">
                        Your first file will have the last 90 days of Brand New Businesses. Each week we'll send the newest Brand New Businesses via email for as long as subscription is valid
                      </label>
                      </div>
                      {zipcodes && (
                        <div className="bg-[#d4e2e9] border-2 border-[#F09900] rounded-xl p-4 flex justify-between items-center mt-4">
                          <div className="flex flex-col">
                            <span className="text-xs font-bold text-[#4D6877] uppercase tracking-wider">Estimated Businesses / Month</span>
                            <span className="text-2xl font-black text-[#F09900] min-h-[32px] flex items-center">
                              {averageLoading ? (
                                <span className="inline-block w-6 h-6 border-2 border-[#F09900]/40 border-t-[#F09900] rounded-full animate-spin" />
                              ) : (
                                formatCount(leadsCount)
                              )}
                            </span>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-xs font-bold text-[#4D6877] uppercase tracking-wider">Monthly Cost</span>
                            <span className="text-2xl font-black text-[#F09900]">
                              ${(selectedPrice / 100)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                     <div className="my-4">
                      <p className="text-md text-[#4E6978] font-medium block leading-6">
                        Each Brand New Business includes:
                        <div className="ml-7 leading-7 my-1">
                        <li>Company Name</li>
                          <li>Address, City, State, Zipcode</li>
                          <li>Phone</li>
                          </div>
                        <p>Delivered in the csv file every week</p>
                      </p>
                      </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Full Name <span className="text-red-600">*</span>
                        </label>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="John Doe"
                          autoComplete="name"
                          required
                          className="w-full py-3 px-3 text-base border rounded-xl outline-none border-gray-300 focus:border-indigo-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Email Address <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          autoComplete="email"
                          required
                          className="w-full py-3 px-3 text-base border rounded-xl outline-none border-gray-300 focus:border-indigo-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Phone Number <span className="text-red-600">*</span>
                        </label>
                        <input
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          inputMode="tel"
                          autoComplete="tel"
                          required
                          className="w-full py-3 px-3 text-base border rounded-xl outline-none border-gray-300 focus:border-indigo-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          Street Address <span className="text-red-600">*</span>
                        </label>
                        <input
                          value={line1}
                          onChange={(e) => setLine1(e.target.value)}
                          placeholder="123 Main St"
                          autoComplete="address-line1"
                          required
                          className="w-full py-3 px-3 text-base border rounded-xl outline-none border-gray-300 focus:border-indigo-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          City <span className="text-red-600">*</span>
                        </label>
                        <input
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          placeholder="City"
                          autoComplete="address-level2"
                          required
                          className="w-full py-3 px-3 text-base border rounded-xl outline-none border-gray-300 focus:border-indigo-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          State <span className="text-red-600">*</span>
                        </label>
                        <input
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          placeholder="State"
                          autoComplete="address-level1"
                          required
                          className="w-full py-3 px-3 text-base border rounded-xl outline-none border-gray-300 focus:border-indigo-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          ZIP Code <span className="text-red-600">*</span>
                        </label>
                        <input
                          value={postal}
                          onChange={(e) => setPostal(e.target.value)}
                          placeholder="ZIP"
                          inputMode="numeric"
                          autoComplete="postal-code"
                          required
                          className="w-full py-3 px-3 text-base border rounded-xl outline-none border-gray-300 focus:border-indigo-500"
                        />
                      </div>
                    </div>

                    {/* Payment Element renders Card, Afterpay, Affirm, Klarna, Cash App Pay, ACH, WeChat Pay, etc */}
                    <div className="space-y-2">
                      {/* <label className="text-sm font-medium text-gray-700">
                        Payment method
                      </label> */}
                      <div className="border rounded-xl p-3">
                        <PaymentElement options={{ layout: "tabs" }} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Promo Code</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={handlePromoCodeChange}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handlePromoCodeSubmit())}
                          placeholder="Enter promo code"
                          className="flex-1 py-2 px-3 text-base border rounded-lg outline-none border-gray-300 focus:border-[#F09900]"
                          disabled={promoCodeValidating || rateLimited}
                        />
                        <button
                          type="button"
                          onClick={handlePromoCodeSubmit}
                          disabled={promoCodeValidating || !promoCode.trim() || rateLimited}
                          className="px-4 py-2 bg-[#F09900] text-white rounded-lg hover:bg-[#E08900] disabled:opacity-50 disabled:cursor-not-allowed font-medium cursor-pointer"
                        >
                          {promoCodeValidating ? "Validating..." : "Apply"}
                        </button>
                      </div>
                      {promoCodeValidating && <p className="mt-1 text-xs text-gray-500">Validating...</p>}
                      {promoCodeValid && promoCodeApplied && <p className="mt-1 text-xs text-green-600 font-medium">✓ Promo code applied! {Math.round(promoCodeDiscount * 100)}% off</p>}
                      {promoCodeError && !rateLimited && <p className="mt-1 text-xs text-red-600">{promoCodeError}</p>}
                      {rateLimited && (
                        <p className="mt-1 text-xs text-red-600 font-medium">
                          Too many attempts. Wait {Math.floor(remainingTime / 60)}:{String(remainingTime % 60).padStart(2, "0")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-1">
                    <input
                      id="accept"
                      type="checkbox"
                      checked={accepted}
                      onChange={(e) => setAccepted(e.target.checked)}
                      className="mt-2 h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
                      aria-describedby="terms-help"
                    />
                    <label
                      htmlFor="accept"
                      className="text-sm text-gray-700 leading-relaxed"
                    >
                      I've read and accept the{" "}
                      <button
                        type="button"
                        className="font-medium underline-offset-2 underline cursor-pointer"
                        onClick={() => setShowTerms(true)}
                        aria-label="Read the full Terms and Conditions"
                        style={{ color: "#4D6877" }}
                      >
                        Terms and conditions
                      </button>
                    </label>
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    disabled={
                      !stripe ||
                      submitting ||
                      !accepted
                    }
                    className="w-full py-4 rounded-xl bg-[#F09900] text-white font-bold text-xl shadow hover:bg-[#E08900] cursor-pointer disabled:opacity-60 transition-colors"
                  >
                    {submitting ? "Processing..." : `Pay $${(discountedAmount / 100).toFixed(2)}`}
                  </button>
                </div>
              </form>

              <div className="flex items-center justify-center pt-6">
                <Lock className="h-4 w-4 text-indigo-500 mr-2 mb-3" />
                <p className="text-xs text-gray-500 mt-1">
                  Your payment details are securely encrypted
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {buyNow === "download" && (
        <div className="w-full max-w-lg mx-auto">
          <div className="shadow-2xl border-6 border-[#F09900] rounded-2xl overflow-hidden bg-white/80 backdrop-blur-sm">
            <div
              className="min-h-[24rem] p-8 flex flex-col items-center mt-3 text-center gap-5"
              role="status"
              aria-live="polite"
            >
              {/* Success icon with subtle ping */}
              <div className="relative">
                <span className="absolute inset-0 rounded-full animate-ping bg-orange-400/40"></span>
                <div className="relative z-10 grid h-16 w-16 place-items-center rounded-full bg-orange-100 text-[#F09900]">
                  <svg
                    className="h-8 w-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
              </div>

              <p className="text-2xl font-bold text-[#F09900] mt-6">
                Payment Successful!
              </p>
              <p className="max-w-sm text-[#4D6877] font-semibold text-lg">
                Thanks for your purchase! Your files are ready to download.
                We’ll also email you the file once the download is complete.
              </p>

              {/* Actions */}
              <div className="mt-2 text-white">
                <div className="flex items-center gap-3 ml-1 mb-4">
                  <input
                    id="accept"
                    type="checkbox"
                    checked={downloadAccepted}
                    onChange={(e) => setDownloadAccepted(e.target.checked)}
                    className="mt-2 h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
                    aria-describedby="terms-help"
                  />
                  <label
                    htmlFor="accept"
                    className="text-sm text-gray-700 leading-relaxed"
                  >
                    I've read and accept the{" "}
                    <button
                      type="button"
                      className="font-medium underline-offset-2 underline cursor-pointer"
                      onClick={() => setDownloadShowTerms(true)}
                      aria-label="Read the full Terms and Conditions"
                      style={{ color: "blue" }}
                    >
                      Terms & Conditions
                    </button>
                  </label>
                </div>
                <button
                  onClick={handleDownload}
                  disabled={loadings || !downloadAccepted}
                  aria-busy={loadings ? "true" : "false"}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F09900] px-9 py-4 font-semibold text-[#1465B7] shadow transition
                       hover:bg-[#d38b0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2
                       disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                >
                  {loadings && (
                    <span
                      className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent text-white"
                      aria-hidden="true"
                      style={{ color: "white" }}
                    />
                  )}
                  {loadings ? "Preparing download…" : "Download now"}
                </button>
              </div>

              {/* Optional progress hint while loading */}
              {loadings && (
                <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-[#F09900]"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <Modal
        title="Terms & Conditions"
        open={showTerms}
        centered // <-- key line (v5)
        onCancel={() => setShowTerms(false)}
        footer={[
          <div className="my-5 mx-5 gap-12">
            <Button
              key="agree"
              type="primary"
              onClick={handleAgree}
              className="!h-10 w-32 !rounded-full mr-12 text-base"
              style={{
                marginRight: 30,
                backgroundColor: "#F09900",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Agree
            </Button>
            ,
            <Button
              key="cancel"
              onClick={() => setShowTerms(false)}
              className="!h-10 w-32 !rounded-full ml-12 bg-red-400"
              style={{
                backgroundColor: "red",
                color: "white",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            ,
          </div>,
        ]}
        width={720}
      >
        <TermsAndCondition />
      </Modal>

      <Modal
        title="Terms & Conditions"
        open={downloadShowTerms}
        centered // <-- key line (v5)
        onCancel={() => setDownloadShowTerms(false)}
        footer={[
          <div className="my-5 mx-5 gap-12">
            <Button
              key="agree"
              type="primary"
              onClick={handleDownloadAgree}
              className="!h-10 w-32 !rounded-full mr-12 text-base"
              style={{
                marginRight: 30,
                backgroundColor: "#F09900",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Agree
            </Button>
            ,
            <Button
              key="cancel"
              onClick={() => setDownloadShowTerms(false)}
              className="!h-10 w-32 !rounded-full ml-12 bg-red-400"
              style={{
                backgroundColor: "red",
                color: "white",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            ,
          </div>,
        ]}
        width={720}
      >
        <TermsAndCondition />
      </Modal>
    </div>
  );
}

/* -------------------- Wrapper -------------------- */

export default function StripePayment() {
  const [stripePromise, setStripePromise] = useState(null);
  const { clientSecret, setClientSecret } = useAppContext();
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const key = await fetchPublishableKeyIfNeeded();
        if (!key || !key.startsWith("pk_"))
          throw new Error("Invalid publishable key (must start with pk_)");
        setStripePromise(loadStripe(key));
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const appearance = {
    theme: "stripe",
  };

  const options = {
    clientSecret,
    appearance,
  };
  return (
    <div>
      {error && (
        <div className="mx-auto my-4 max-w-xl rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">
          <b>Configuration error</b>
          <div className="mt-1 text-sm">{error}</div>
        </div>
      )}

      {stripePromise && clientSecret && (
        <Elements stripe={stripePromise} options={options} key={clientSecret}>
          <PrettyForm />
        </Elements>
      )}
    </div>
  );
}
